import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import {
  Field,
  HTMLWrapper,
  RadioGroup,
  Radio,
  Wrap,
  WrapItem,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "@/types";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface RadioButtonQuestionProps {
  question: Question;
}

export const validate = async (
  value: string,
  question: Question
): Promise<string[]> => {
  let schema = string();
  if (question.required) {
    schema = schema.required();
  }
  try {
    await schema.validate(value);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const RadioButtonQuestion = ({
  question
}: RadioButtonQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || null;

  const handleChange = (value: string): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: parseInt(value)
      })
    );
  };

  return (
    <Field.Root
      my={4}
      as="fieldset"
      id={`${question.id}`}
      invalid={error.length > 0}
      required={question.required}
      disabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <Field.Label as="legend">
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </Field.Label>
      <RadioGroup
        onValueChange={(details) => handleChange(details.value ?? "")}
        value={answer}
        size={{ base: "lg", lg: "md" }}
      >
        <Wrap
          gap={4}
          direction={{
            base: "column",
            lg: question.horizontal ? "row" : "column"
          }}
        >
          {[...question.questionOptions]
            .filter((option) => !option.deleted)
            .sort((a, b) => a.sequence - b.sequence)
            .map((option) => (
              <WrapItem key={option.id}>
                <Radio
                  pl={1}
                  key={option.id}
                  value={option.id}
                  name={`radioButtonQuestion-${question.id}`}
                  data-testid={`radioButtonQuestion-${option.name}`}
                >
                  {option.name}
                </Radio>
              </WrapItem>
            ))}
        </Wrap>
      </RadioGroup>
      {error.length > 0 && (
        <Field.ErrorText data-testid="radioButtonQuestion-errorMessage">
          <Field.ErrorIcon />
          {translate(`errors.${error}`)}
        </Field.ErrorText>
      )}
      <Field.HelperText>{question.notes}</Field.HelperText>
    </Field.Root>
  );
};

export default RadioButtonQuestion;
